import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Clock, Info, Lock, Mail } from 'lucide-react';
import Button from '../Button';
import { api } from '../../utils/shipmentsApi';
import { useCountdown } from './useCountdown';
import { CODE_LENGTH, DEFAULT_VALIDITY_MIN, describeError, digitsOnly, formatWait, minutesText } from './gateMessages';

const TONES = {
  error: { bg: '#FFEBEE', fg: '#C62828', Icon: AlertCircle },
  info: { bg: '#E3F2FD', fg: '#1565C0', Icon: Info },
  warn: { bg: '#FFF3E0', fg: '#E65100', Icon: AlertCircle },
};

const Banner = ({ tone, role, children }) => {
  const { bg, fg, Icon } = TONES[tone];
  return (
    <div role={role} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left', backgroundColor: bg, color: fg, borderRadius: 8, padding: '12px 14px', margin: '0 0 16px', fontSize: 14, lineHeight: 1.45 }}>
      <Icon size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
};

const css = `
  .shp-code::placeholder { color: #BDBDBD; }
  .shp-code:focus { border-color: #E53935; box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.12); }
  .shp-link { background: none; border: none; padding: 12px 8px; min-height: 44px; font: inherit; font-size: 14px; font-weight: 600; color: #E53935; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
  .shp-link:disabled { color: #999999; cursor: not-allowed; text-decoration: none; }
`;

// Verification gate of the Zásilky module: send a code to the masked e-mail, then enter the 6 digits.
// Props: status (from GET /verify/status), notice (optional info text), onVerified(data).
const ShipmentsGate = ({ status, notice, onVerified }) => {
  const [step, setStep] = useState('send');
  const [maskedEmail, setMaskedEmail] = useState(status?.maskedEmail || '');
  const [validityMin, setValidityMin] = useState(DEFAULT_VALIDITY_MIN);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [sentCount, setSentCount] = useState(0);
  const inputRef = useRef(null);
  const busyRef = useRef(false);
  const sendWait = useCountdown();
  const lockWait = useCountdown();

  const mailMissing = status?.mailConfigured === false;
  const locked = lockWait.remaining > 0;
  const sendBlocked = sendWait.remaining > 0 || mailMissing;

  // Focus the code field after every successful send (first send and "Poslat nový kód")
  useEffect(() => {
    if (sentCount > 0) inputRef.current?.focus();
  }, [sentCount]);

  const showFailure = (err) => {
    const info = describeError(err);
    setError(info.text);
    if (info.waitSeconds) {
      sendWait.start(info.waitSeconds);
      if (info.lock) lockWait.start(info.waitSeconds);
    }
    return info;
  };

  const requestCode = async () => {
    if (busyRef.current || sendBlocked) return;
    busyRef.current = true;
    setSending(true);
    setError('');
    try {
      const data = await api('/verify/request', { method: 'POST' });
      if (data?.maskedEmail) setMaskedEmail(data.maskedEmail);
      if (data?.expiresInSeconds) setValidityMin(Math.max(1, Math.round(data.expiresInSeconds / 60)));
      sendWait.start(data?.cooldownSeconds ?? 60);
      setCode('');
      setStep('code');
      setSentCount((n) => n + 1);
    } catch (err) {
      showFailure(err);
    } finally {
      busyRef.current = false;
      setSending(false);
    }
  };

  const verify = async (value) => {
    if (busyRef.current || locked) return;
    if (value.length !== CODE_LENGTH) {
      setError(`Zadejte všech ${CODE_LENGTH} číslic kódu.`);
      inputRef.current?.focus();
      return;
    }
    busyRef.current = true;
    setVerifying(true);
    setError('');
    try {
      const data = await api('/verify/confirm', { method: 'POST', body: { code: value } });
      onVerified?.(data);
    } catch (err) {
      const info = showFailure(err);
      if (info.clearCode) setCode('');
      inputRef.current?.focus();
    } finally {
      busyRef.current = false;
      setVerifying(false);
    }
  };

  const applyCode = (next) => {
    setCode(next);
    if (error) setError('');
    if (next.length === CODE_LENGTH) verify(next);
  };

  const handleChange = (e) => applyCode(digitsOnly(e.target.value));

  // maxLength would cut "123 456" at 6 raw characters, so pasting is handled here and non-digits are stripped
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = digitsOnly(e.clipboardData.getData('text'));
    if (!pasted) return;
    const { selectionStart: from, selectionEnd: to } = e.target;
    applyCode(pasted.length >= CODE_LENGTH ? pasted : digitsOnly(code.slice(0, from) + pasted + code.slice(to)));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    verify(code);
  };

  const target = maskedEmail ? <strong style={{ color: '#333333' }}>{maskedEmail}</strong> : 'váš e-mail';

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 'clamp(16px, 5vw, 48px) 16px' }}>
      <style>{css}</style>
      <section aria-labelledby="shipments-gate-title" style={{ width: '100%', maxWidth: 440, textAlign: 'center', backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: 12, padding: 'clamp(24px, 6vw, 36px) clamp(20px, 6vw, 32px)', boxShadow: 'var(--shadow-md)' }}>
        <div aria-hidden="true" style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#FFEBEE', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Lock size={26} color="#E53935" />
        </div>
        <h1 id="shipments-gate-title" style={{ fontSize: 22, fontWeight: 700, letterSpacing: 0, color: '#333333', margin: '0 0 8px' }}>Modul Zásilky je zabezpečený</h1>
        <p style={{ fontSize: 14, color: '#666666', margin: '0 0 24px', lineHeight: 1.5 }}>
          Pro vstup potřebujete ověřovací kód. {step === 'send' ? 'Pošleme ho na' : 'Poslali jsme ho na'} {target}.
        </p>

        {notice && <Banner tone="info" role="status">{notice}</Banner>}
        {mailMissing && <Banner tone="warn">Odesílání e-mailů není nastavené. Kontaktujte správce.</Banner>}
        {error && <Banner tone="error" role="alert">{error}</Banner>}

        {step === 'send' ? (
          <>
            <Button variant="primary" size="lg" fullWidth icon={Mail} loading={sending} disabled={sendBlocked} onClick={requestCode}>
              {sendWait.remaining > 0 ? `Znovu za ${formatWait(sendWait.remaining)}` : 'Poslat ověřovací kód'}
            </Button>
            <p style={{ fontSize: 13, color: '#666666', margin: '12px 0 0' }}>Kód platí {minutesText(validityMin)} a jde použít jen jednou.</p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate style={{ textAlign: 'left' }}>
            <label htmlFor="shipments-gate-code" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#333333', marginBottom: 6 }}>Šestimístný kód z e-mailu</label>
            <input
              id="shipments-gate-code" ref={inputRef} className="shp-code" name="code" type="text"
              inputMode="numeric" autoComplete="one-time-code" maxLength={CODE_LENGTH} pattern="[0-9]*"
              enterKeyHint="done" spellCheck={false} autoCapitalize="off" autoCorrect="off"
              placeholder="000000" value={code} onChange={handleChange} onPaste={handlePaste}
              readOnly={verifying || locked} aria-invalid={error ? 'true' : undefined} aria-describedby="shipments-gate-hint"
              style={{ width: '100%', height: 60, border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`, borderRadius: 8, backgroundColor: locked ? '#F5F5F5' : '#FFFFFF', color: '#333333', outline: 'none', fontFamily: 'inherit', fontSize: 28, fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.5em', paddingLeft: '0.5em', textAlign: 'center' }}
            />
            <p id="shipments-gate-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#666666', margin: '8px 0 20px' }}>
              <Clock size={14} aria-hidden="true" /> Kód platí {minutesText(validityMin)} a jde použít jen jednou.
            </p>
            <Button type="submit" variant="primary" size="lg" fullWidth loading={verifying} disabled={locked}>
              {locked ? `Zkuste to za ${formatWait(lockWait.remaining)}` : 'Ověřit'}
            </Button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" className="shp-link" onClick={requestCode} disabled={sending || sendBlocked}>
                Poslat nový kód{sendWait.remaining > 0 ? ` (za ${formatWait(sendWait.remaining)})` : ''}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default ShipmentsGate;
